import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppFiltersProvider } from "@/state/AppFilters";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthProvider } from "@/hooks/useAuth";
import { RequireAuth } from "@/components/RequireAuth";
// Auth is the landing destination for all unauthenticated visitors (the path
// Lighthouse measures from `/`). Eager-importing it removes an extra request
// hop in the critical network chain without meaningfully growing the initial
// bundle, since logged-out users always need it on first paint.
import AuthPage from "./pages/Auth";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

const ResetPasswordPage = lazy(() => import("./pages/ResetPassword"));
const UnsubscribePage = lazy(() => import("./pages/Unsubscribe"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transactions = lazy(() => import("./pages/Transactions"));
const DailyCloseRoute = lazy(() => import("./pages/DailyCloseRoute"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Reorders = lazy(() => import("./pages/Reorders"));
const ReorderSuggestions = lazy(() => import("./pages/ReorderSuggestions"));
const PurchaseOrderPrint = lazy(() => import("./pages/PurchaseOrderPrint"));
const PricebookList = lazy(() => import("./pages/PricebookList"));
const PricebookDashboard = lazy(() => import("./pages/PricebookDashboard"));
const PricebookItems = lazy(() => import("./pages/PricebookItems"));
const PricebookGroups = lazy(() => import("./pages/PricebookGroups"));
const PricebookModifiers = lazy(() => import("./pages/PricebookModifiers"));
const PricebookForm = lazy(() => import("./pages/PricebookForm"));
const PricebookImports = lazy(() => import("./pages/PricebookImports"));
const PricebookImportDetail = lazy(() => import("./pages/PricebookImportDetail"));
const PricebookInvoiceDraftQueue = lazy(() => import("./pages/PricebookInvoiceDraftQueue"));
const DepartmentTaxRules = lazy(() => import("./pages/DepartmentTaxRules"));
const Promotions = lazy(() => import("./pages/Promotions"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Banking = lazy(() => import("./pages/Banking"));
const Reports = lazy(() => import("./pages/Reports"));
const Lottery = lazy(() => import("./pages/Lottery"));
const Integrations = lazy(() => import("./pages/Integrations"));
const PosIntegrations = lazy(() => import("./pages/PosIntegrations"));
const Support = lazy(() => import("./pages/Support"));
const GilbarcoPassportSetup = lazy(() => import("./pages/GilbarcoPassportSetup"));
const PrelaunchChecklist = lazy(() => import("./pages/PrelaunchChecklist"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

export function AppLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground" role="status">
      <div className="rounded-md border border-border bg-card/80 px-5 py-4 text-center shadow-sm">
        <div className="text-sm font-medium">Loading StoreLedger Pro...</div>
        <div className="mt-1 text-xs text-muted-foreground">Opening a safe workspace view.</div>
      </div>
    </div>
  );
}

export function AppRouteErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground" role="alert">
      <div className="max-w-md rounded-md border border-border bg-card/80 p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold">Could not load this page</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          StoreLedger Pro hit a route loading error. Your data was not changed.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <a className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground" href="/">
            Back to Dashboard
          </a>
          <button
            type="button"
            className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}

type AppRouteErrorBoundaryState = { hasError: boolean };

class AppRouteErrorBoundary extends Component<{ children: ReactNode }, AppRouteErrorBoundaryState> {
  state: AppRouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppRouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Route loading error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) return <AppRouteErrorFallback />;
    return this.props.children;
  }
}

export function AppRoutes() {
  return (
    <AppRouteErrorBoundary>
      <Suspense fallback={<AppLoadingFallback />}>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route element={<RequireAuth />}>
            <Route path="/reorders/po/:id/print" element={<PurchaseOrderPrint />} />
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/daily-close" element={<DailyCloseRoute />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/reorders" element={<Reorders />} />
              <Route path="/reorders/suggestions" element={<ReorderSuggestions />} />
              <Route path="/pricebook" element={<PricebookDashboard />} />
              <Route path="/pricebook/items" element={<PricebookItems />} />
              <Route path="/pricebook/groups" element={<PricebookGroups />} />
              <Route path="/pricebook/modifiers" element={<PricebookModifiers />} />
              <Route path="/pricebook/legacy" element={<PricebookList />} />
              <Route path="/pricebook/new" element={<PricebookForm />} />
              <Route path="/pricebook/imports" element={<PricebookImports />} />
              <Route path="/pricebook/imports/:id/draft-queue" element={<PricebookInvoiceDraftQueue />} />
              <Route path="/pricebook/imports/:id" element={<PricebookImportDetail />} />
              <Route path="/pricebook/:id" element={<PricebookForm />} />
              <Route path="/settings/departments" element={<DepartmentTaxRules />} />
              <Route path="/promotions" element={<Promotions />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/banking" element={<Banking />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/lottery" element={<Lottery />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/integrations/pos" element={<PosIntegrations />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/gilbarco-passport-setup" element={<GilbarcoPassportSetup />} />
              <Route path="/support/prelaunch-checklist" element={<PrelaunchChecklist />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppRouteErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner theme="dark" />
      <BrowserRouter>
        <AuthProvider>
          <AppFiltersProvider>
            <AppRoutes />
          </AppFiltersProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
